import {
  Box,
  Button,
  Checkbox,
  Container,
  Link,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";

import { BankAvatar, BankLogo } from "@dfp/react-components";

import { makeScaInstructionNumber } from "../demoBankConnect.utils.ts";
import type { BankFlowProps, BankFlowStep } from "./bankFlow.types.ts";

// Replica of Komerční banka's PSD2 pages (see Confluence "Screen flow when
// connecting to a bank" → KB). The copy is intentionally hard-coded Czech —
// these are external bank pages, not localized app UI.
const KB_RED = "#E2001A";
const KB_RED_DARK = "#B30015";
const KB_TEXT = "#1A1A1A";
const KB_MUTED = "#5A5A5A";
const KB_BORDER = "#D6D6D6";

const FONT_STACK = '"Helvetica Neue", Helvetica, Arial, sans-serif';

/**
 * KB shows a short lowercase "kontrolní kód" in its mobile-app confirmation.
 * Derive ~4 stable chars from the connection so a connection always renders the
 * same code (base36 of the SCA instruction number, lowercased).
 */
function makeScaControlCode(connectionId: string): string {
  return Number(makeScaInstructionNumber(connectionId))
    .toString(36)
    .toLowerCase()
    .slice(-4);
}

/** Komerční banka PSD2 flow: login → SCA mobile approval → account consent. */
export function KbFlow({
  thirdPartyName,
  ownerName,
  username,
  connectionId,
  accounts,
  initialStep,
  onConnect,
  onCancel,
}: BankFlowProps) {
  const [step, setStep] = useState<BankFlowStep>(initialStep);
  const [loginName, setLoginName] = useState(username);
  const [remember, setRemember] = useState(true);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    () => new Set(accounts.map((account) => account.id)),
  );

  // The mobile-app approval "arrives" by itself after a moment, like the real
  // KB+ / KB Klíč confirmation would.
  useEffect(() => {
    if (step !== "sca") return;
    const timer = setTimeout(() => setStep("accounts"), 3000);
    return () => clearTimeout(timer);
  }, [step]);

  const toggle = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const redButtonSx = {
    bgcolor: KB_RED,
    color: "#fff",
    borderRadius: 1.5,
    fontFamily: FONT_STACK,
    fontWeight: 700,
    textTransform: "none",
    px: 3.5,
    py: 1,
    boxShadow: "none",
    "&:hover": { bgcolor: KB_RED_DARK, boxShadow: "none" },
  };

  const outlinedButtonSx = {
    color: KB_TEXT,
    borderColor: KB_BORDER,
    borderRadius: 1.5,
    fontFamily: FONT_STACK,
    fontWeight: 600,
    textTransform: "none",
    px: 3,
    py: 1,
    "&:hover": { bgcolor: "#f5f5f5", borderColor: "#bcbcbc" },
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#fff", fontFamily: FONT_STACK }}>
      <Container maxWidth="lg" sx={{ py: 5 }}>
        {step === "login" && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "5fr 6fr" },
              gap: { xs: 6, md: 10 },
              alignItems: "center",
              mt: { xs: 2, md: 6 },
            }}
          >
            {/* Stylized 3D "portal frame" with the KB lockup centered inside. */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Box
                sx={{
                  width: { xs: 240, md: 300 },
                  height: { xs: 320, md: 400 },
                  borderRadius: 8,
                  p: "14px",
                  background: `linear-gradient(145deg, ${KB_RED} 0%, ${KB_RED_DARK} 55%, #7A000E 100%)`,
                  boxShadow:
                    "0 30px 60px rgba(226,0,26,0.28), inset 0 2px 6px rgba(255,255,255,0.35)",
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    borderRadius: 6,
                    bgcolor: "#fff",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 2.5,
                    boxShadow: "inset 0 1px 4px rgba(0,0,0,0.08)",
                  }}
                >
                  <BankLogo
                    bank="KB"
                    style={{ height: 44, width: "auto", display: "block" }}
                  />
                </Box>
              </Box>
            </Box>

            <Box sx={{ maxWidth: 460 }}>
              <Typography
                sx={{
                  color: KB_TEXT,
                  fontWeight: 800,
                  fontSize: 40,
                  fontFamily: FONT_STACK,
                  mb: 4,
                }}
              >
                Přihlášení
              </Typography>

              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: KB_MUTED,
                  fontFamily: FONT_STACK,
                  mb: 0.75,
                }}
              >
                Uživatelské jméno
              </Typography>
              <TextField
                fullWidth
                value={loginName}
                onChange={(event) => setLoginName(event.target.value)}
                sx={{
                  bgcolor: "#fff",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 1.5,
                    "& fieldset": { borderColor: KB_BORDER },
                    "&:hover fieldset": { borderColor: "#bcbcbc" },
                    "&.Mui-focused fieldset": { borderColor: KB_RED },
                  },
                  "& input": {
                    py: 1.4,
                    px: 1.75,
                    fontSize: 16,
                    fontFamily: FONT_STACK,
                  },
                }}
              />

              <Stack
                direction="row"
                sx={{ alignItems: "center", gap: 1, mt: 2 }}
              >
                <Switch
                  checked={remember}
                  onChange={(event) => setRemember(event.target.checked)}
                  sx={{
                    "& .Mui-checked": { color: KB_RED },
                    "& .Mui-checked + .MuiSwitch-track": {
                      backgroundColor: `${KB_RED} !important`,
                    },
                  }}
                />
                <Typography
                  sx={{
                    fontSize: 15,
                    color: KB_TEXT,
                    fontFamily: FONT_STACK,
                  }}
                >
                  Zapamatovat
                </Typography>
              </Stack>

              <Stack
                direction="row"
                sx={{ alignItems: "center", gap: 1.5, mt: 4, flexWrap: "wrap" }}
              >
                <Button
                  variant="contained"
                  onClick={() => setStep("sca")}
                  disabled={!loginName}
                  sx={redButtonSx}
                >
                  Pokračovat
                </Button>
                <Button variant="outlined" sx={outlinedButtonSx}>
                  Nepamatuji si uživatelské jméno
                </Button>
              </Stack>

              <Box sx={{ mt: 4 }}>
                <Link
                  sx={{
                    color: KB_RED,
                    fontSize: 15,
                    fontWeight: 600,
                    fontFamily: FONT_STACK,
                    cursor: "pointer",
                    textDecoration: "none",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  🪪 Osobní certifikát na čipové kartě
                </Link>
              </Box>
            </Box>
          </Box>
        )}

        {step === "sca" && (
          <Box>
            <Stack direction="row" sx={{ justifyContent: "flex-end", mb: 2 }}>
              <Link
                sx={{
                  color: KB_MUTED,
                  fontSize: 14,
                  fontFamily: FONT_STACK,
                  cursor: "pointer",
                  textDecoration: "none",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                English
              </Link>
            </Stack>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "5fr 6fr" },
                gap: { xs: 6, md: 10 },
                alignItems: "center",
                mt: { xs: 2, md: 4 },
              }}
            >
              {/* Light panel with a phone graphic showing a red key. */}
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <Box
                  sx={{
                    bgcolor: "#F6F4F2",
                    borderRadius: 6,
                    p: { xs: 4, md: 6 },
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <Box
                    sx={{
                      width: 170,
                      height: 320,
                      borderRadius: 5,
                      bgcolor: "#fff",
                      border: "8px solid #2A2A2A",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 20px 40px rgba(0,0,0,0.12)",
                    }}
                  >
                    <Box
                      sx={{
                        width: 96,
                        height: 96,
                        borderRadius: "50%",
                        bgcolor: "rgba(226,0,26,0.10)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography sx={{ fontSize: 48, lineHeight: 1 }}>
                        🔑
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>

              <Box sx={{ maxWidth: 520 }}>
                <Link
                  onClick={onCancel}
                  sx={{
                    color: KB_MUTED,
                    fontSize: 15,
                    fontWeight: 600,
                    fontFamily: FONT_STACK,
                    cursor: "pointer",
                    textDecoration: "none",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  ‹ Zpět
                </Link>

                <Typography
                  sx={{
                    color: KB_TEXT,
                    fontWeight: 800,
                    fontSize: 30,
                    fontFamily: FONT_STACK,
                    mt: 2,
                    mb: 2,
                  }}
                >
                  Potvrďte přihlášení v mobilní aplikaci
                </Typography>

                <Typography
                  sx={{
                    fontSize: 16,
                    color: KB_TEXT,
                    fontFamily: FONT_STACK,
                    mb: 3,
                  }}
                >
                  Otevřete aplikaci KB+ nebo KB Klíč a zkontrolujte, že tam
                  vidíte následující kód.
                </Typography>

                <Typography
                  sx={{
                    color: KB_RED,
                    fontWeight: 800,
                    fontSize: 56,
                    letterSpacing: "0.12em",
                    fontFamily: FONT_STACK,
                    mb: 3,
                  }}
                >
                  {makeScaControlCode(connectionId)}
                </Typography>

                <Box
                  sx={{
                    bgcolor: "#F2F6FC",
                    border: "1px solid #D5E2F2",
                    borderRadius: 2,
                    p: 2,
                    mb: 2.5,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 14,
                      color: KB_TEXT,
                      fontFamily: FONT_STACK,
                    }}
                  >
                    ℹ Tento jednorázový kontrolní kód nikam nezadávejte. Není to
                    PIN.
                  </Typography>
                </Box>

                <Link
                  sx={{
                    color: KB_RED,
                    fontSize: 15,
                    fontWeight: 600,
                    fontFamily: FONT_STACK,
                    cursor: "pointer",
                    textDecoration: "none",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  📶 Nemáte mobil připojený k internetu?
                </Link>

                <Box sx={{ mt: 6 }}>
                  <BankAvatar bank="KB" size={32} />
                </Box>
              </Box>
            </Box>
          </Box>
        )}

        {step === "accounts" && (
          <Box sx={{ maxWidth: 720, mx: "auto" }}>
            <Stack
              direction="row"
              sx={{ alignItems: "center", gap: 1.5, mb: 3 }}
            >
              <BankAvatar bank="KB" size={40} />
              <Typography
                sx={{
                  color: KB_TEXT,
                  fontWeight: 800,
                  fontSize: 28,
                  fontFamily: FONT_STACK,
                }}
              >
                Povolení přístupu k účtům
              </Typography>
            </Stack>

            <Typography
              sx={{
                fontSize: 15,
                color: KB_MUTED,
                fontFamily: FONT_STACK,
                mb: 3,
              }}
            >
              Třetí strana:{" "}
              <Box
                component="span"
                sx={{ color: KB_TEXT, fontWeight: 700 }}
              >
                {thirdPartyName}
              </Box>
            </Typography>

            <Box
              data-tour="bank-accounts"
              sx={{
                border: `1px solid ${KB_BORDER}`,
                borderRadius: 2,
                overflow: "hidden",
                mb: 4,
              }}
            >
              {accounts.map((account, index) => (
                <Stack
                  key={account.id}
                  direction="row"
                  onClick={() => toggle(account.id)}
                  sx={{
                    alignItems: "center",
                    gap: 1.5,
                    px: 2,
                    py: 1.75,
                    cursor: "pointer",
                    borderTop:
                      index === 0 ? "none" : `1px solid ${KB_BORDER}`,
                    "&:hover": { bgcolor: "#faf7f7" },
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={selectedIds.has(account.id)}
                    sx={{
                      p: 0,
                      color: KB_RED,
                      "&.Mui-checked": { color: KB_RED },
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: 15,
                      color: KB_TEXT,
                      fontFamily: FONT_STACK,
                    }}
                  >
                    <Box component="span" sx={{ fontWeight: 700 }}>
                      {ownerName}
                    </Box>
                    {"  "}
                    {account.accountNumber}
                    {"  "}
                    <Box component="span" sx={{ color: KB_MUTED }}>
                      ({account.currency})
                    </Box>
                  </Typography>
                </Stack>
              ))}
            </Box>

            <Stack direction="row" sx={{ alignItems: "center", gap: 2 }}>
              <Button
                data-tour="bank-connect-submit"
                variant="contained"
                disabled={selectedIds.size === 0}
                onClick={() =>
                  onConnect(
                    accounts.filter((account) => selectedIds.has(account.id)),
                  )
                }
                sx={redButtonSx}
              >
                Potvrdit
              </Button>
              <Button
                onClick={onCancel}
                sx={{
                  color: KB_MUTED,
                  fontFamily: FONT_STACK,
                  fontWeight: 600,
                  textTransform: "none",
                  "&:hover": { bgcolor: "transparent", color: KB_TEXT },
                }}
              >
                Zrušit
              </Button>
            </Stack>
          </Box>
        )}
      </Container>
    </Box>
  );
}
